import { Button } from "@/components/ui/button";
import { ArrowDown, Sparkles, Heart, Star } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";
const tunisianCollage = "/tunisian-collage-opt.webp";

const words = ["fierté", "passion", "racines", "histoire"];

export const Hero = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const onScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      if (imgRef.current) {
        imgRef.current.style.transform = `scale(1.1) translateY(${window.scrollY * 0.15}px)`;
      }
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onScroll]);

  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center">
      {/* Parallax background */}
      <img
        ref={imgRef}
        src={tunisianCollage}
        alt=""
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-center will-change-transform"
        style={{ transform: "scale(1.1) translateY(0px)" }}
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground/75 via-foreground/45 to-foreground/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary/15 via-transparent to-accent/10" />

      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 w-28 h-28 rounded-full bg-primary/10 blur-2xl animate-float" />
      <div className="absolute bottom-32 right-16 w-36 h-36 rounded-full bg-accent/10 blur-3xl animate-float" style={{ animationDelay: "1.5s" }} />
      <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-full bg-primary/15 blur-xl animate-float" style={{ animationDelay: "0.8s" }} />

      <div className="container mx-auto px-4 py-28 md:py-44 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="animate-fade-in-up inline-flex items-center gap-2 bg-primary-foreground/15 backdrop-blur-md border border-primary-foreground/20 rounded-full px-5 py-2.5 mb-8">
            <Sparkles className="w-4 h-4 text-primary-foreground" />
            <span className="text-sm font-body font-medium text-primary-foreground">
              Adhésifs 3D premium — Fait main au Canada
            </span>
          </div>

          <h1 className="text-7xl md:text-9xl font-display font-bold tracking-tight mb-6 animate-fade-in-up">
            <span className="text-primary-foreground drop-shadow-[0_2px_25px_rgba(0,0,0,0.5)]">
              EL FAZA
            </span>
          </h1>

          <p
            className="text-xl md:text-3xl font-display font-semibold text-primary-foreground mb-2 animate-fade-in-up drop-shadow-[0_1px_10px_rgba(0,0,0,0.4)]"
            style={{ animationDelay: "0.1s" }}
          >
            L'art tunisien en adhésif
          </p>

          {/* Animated rotating word */}
          <div
            className="h-10 md:h-12 overflow-hidden mb-8 animate-fade-in-up"
            style={{ animationDelay: "0.15s" }}
          >
            <p className="text-lg md:text-2xl font-display text-primary-foreground/90 drop-shadow-[0_1px_8px_rgba(0,0,0,0.3)]">
              Portez vos{" "}
              <span
                key={wordIndex}
                className="inline-block text-primary-foreground font-bold border-b-2 border-primary-foreground/60 animate-fade-in-up"
              >
                {words[wordIndex]}
              </span>
              {" "}partout 🇹🇳
            </p>
          </div>

          <p
            className="text-sm md:text-base font-body text-primary-foreground/85 max-w-lg mx-auto mb-12 leading-relaxed animate-fade-in-up drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]"
            style={{ animationDelay: "0.2s" }}
          >
            Chaque adhésif raconte une histoire de la Tunisie.
            <br className="hidden md:block" />
            Créé au Canada 🍁 avec amour, fierté et gratitude.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
            <Button
              size="lg"
              className="font-display text-base px-10 py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
            >
              Découvrir nos adhésifs
              <ArrowDown className="w-4 h-4 ml-1" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="font-display text-base px-8 py-6 bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/20 backdrop-blur-sm"
              onClick={() => {
                const el = document.querySelector("[data-newsletter]");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Restez à jour 📬
            </Button>
          </div>

          {/* Stats — refined */}
          <div
            className="mt-16 flex justify-center gap-10 md:gap-20 animate-fade-in-up"
            style={{ animationDelay: "0.5s" }}
          >
            {[
              { icon: Star, value: "3D", label: "Relief premium" },
              { emoji: "🍁", value: "Canada", label: "Fabriqué ici" },
              { icon: Heart, value: "🇹🇳", label: "Fierté tunisienne" },
            ].map((stat) => (
              <div key={stat.label} className="text-center group">
                <div className="text-2xl md:text-3xl font-display font-bold text-primary-foreground drop-shadow-md group-hover:scale-110 transition-transform">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm font-body text-primary-foreground/70 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-primary-foreground/40 flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-primary-foreground/60 rounded-full animate-float" />
        </div>
      </div>
    </section>
  );
};
